import {
  ChangeEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebounce from 'react-use/lib/useDebounce';

import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  makeStyles,
  styled,
} from '@material-ui/core';

import {
  ConfidenceWebsiteLink,
  FormFieldsBox,
  LoadingOverlayMui as LoadingOverlay,
  WizardStepper,
  useAlert,
  useAuth,
  useSessionStorage,
} from '@spotify/confidence-core-react';

import { TransitionScreen } from '../TransitionScreen';
import { ConfidenceLogoHorizontal } from './ConfidenceLogoHorizontal';
import { ExperimentationLoader } from './ExperimentationLoader';
import { IntroStep } from './IntroStep';
import { UserGoals } from './UserGoals';
import { UserRole } from './UserRole';
import {
  CreatedAccount,
  InviteResponse,
  useSignupTracking,
  useValidateAccountName,
} from './hooks';
import { getSignupAttribution } from './hooks/attribution';
import {
  AuthConnection,
  CreateAccountRequest,
  Region,
  useCreateAccount,
} from './hooks/useCreateAccount';
import CreateWorkspace from './img/create_workspace.png';
import MadeForBusinesses from './img/made_for_businesses.png';

export type AccountDetailsState = Omit<
  CreateAccountRequest,
  'authConnections' | 'allowedLoginEmailDomains' | 'billingDetails'
> & {
  authConnections: Record<AuthConnection, boolean>;
  loginDomain: string;
  userRole: string;
  userGoals: string[];
};

const DEFAULT_STATE: AccountDetailsState = {
  displayName: '',
  loginId: '',
  region: Region.RegionEu,
  loginDomain: '',
  authConnections: {
    googleAuthConnection: false,
    passwordAuthConnection: false,
  },
  userRole: '',
  userGoals: [],
};
const BrandingWrapper = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: theme.spacing(2),
  height: 64,
  [theme.breakpoints.up('sm')]: {
    left: 0,
  },
}));

export const Branding = () => (
  <BrandingWrapper>
    <ConfidenceLogoHorizontal />
  </BrandingWrapper>
);

const UserInfoWrapper = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: theme.spacing(2),
  height: 64,
  display: 'flex',
  alignItems: 'center',
  zIndex: 10001,
  pointerEvents: 'auto',
}));

const useUserInfoStyles = makeStyles(theme => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    border: `2px solid ${theme.palette.divider}`,
  },
  popoverContent: {
    padding: theme.spacing(6, 10),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  popoverAvatar: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    border: `2px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
}));

type Auth0User = {
  name?: string;
  email?: string;
  picture?: string;
};

function UserInfoButton({ user }: { user: Auth0User }) {
  const classes = useUserInfoStyles();
  const { logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    logout({ logoutParams: { returnTo: `${window.location.origin}/signup` } });
  };

  return (
    <UserInfoWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classes.button}>
        <Avatar
          className={classes.avatar}
          data-csr-block
          src={user.picture}
          imgProps={{ referrerPolicy: 'no-referrer' }}
        />
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="body1" noWrap data-csr-mask>
            {user.name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            noWrap
            data-csr-mask
          >
            {user.email}
          </Typography>
        </Box>
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMouseLeave}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{ zIndex: 10001 }}
        disableRestoreFocus
        PaperProps={{
          onMouseEnter: () => setAnchorEl(anchorEl),
          onMouseLeave: handleMouseLeave,
        }}
      >
        <Box className={classes.popoverContent}>
          <Avatar
            className={classes.popoverAvatar}
            data-csr-block
            src={user.picture}
            imgProps={{ referrerPolicy: 'no-referrer' }}
          />
          <Typography variant="body1" data-csr-mask>
            {user.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" data-csr-mask>
            {user.email}
          </Typography>
          <Box marginTop={2}>
            <Button size="small" variant="outlined" onClick={handleSignOut}>
              Sign out
            </Button>
          </Box>
        </Box>
      </Popover>
    </UserInfoWrapper>
  );
}

const NAME_MAX_LENGTH = 21;
const NAME_MIN_LENGTH = 3;

const isDisplayNameValid = (displayName: string) => {
  const displayNameRegex = new RegExp(
    /[a-zA-Z][a-zA-Z0-9\\s\\-]{1,20}[a-zA-Z0-9]/,
  );

  return (
    displayNameRegex.test(displayName) &&
    displayName.length >= NAME_MIN_LENGTH &&
    displayName.length <= NAME_MAX_LENGTH
  );
};

const formatDisplayName = (displayName: string) => {
  return displayName.replace(/[^a-zA-Z0-9 -]/g, '').slice(0, NAME_MAX_LENGTH);
};

const formatLoginId = (loginId: string = '') => {
  return loginId
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase()
    .slice(0, NAME_MAX_LENGTH);
};

const isLoginIdValid = (loginId: string) => {
  const loginIdRegex = new RegExp(/[a-z][a-z0-9\\-]{1,19}[a-z0-9]$/);
  return (
    loginIdRegex.test(loginId) &&
    loginId.length >= NAME_MIN_LENGTH &&
    loginId.length <= NAME_MAX_LENGTH
  );
};

const isEmailNotVerifiedError = (error?: Error) => {
  return Boolean(error?.message.includes('Email not verified'));
};

const isAccountUnderReviewError = (error?: Error) => {
  return Boolean(error && /review/i.test(error.message));
};

export const CreateAccountForm = ({
  accountInvite,
  inviteId,
  onCreate,
  token,
  skipCompanyWizard,
}: {
  token?: string;
  accountInvite?: InviteResponse;
  inviteId?: string;
  skipCompanyWizard?: boolean;
  onCreate: (account?: CreatedAccount) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const alert = useAlert();
  const [step, setStep] = useState(skipCompanyWizard ? 2 : 0);
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithPopup,
    getAccessTokenSilently,
    error: authError,
  } = useAuth();

  const [{ value: createdAccount, error }, createAccount] =
    useCreateAccount(inviteId);
  const [showOverlay, setShowOverlay] = useState(false);

  const tracking = useSignupTracking('company_wizard', {
    enabled: isAuthenticated,
  });
  const {
    trackStep,
    trackCompletion,
    trackUserIntent,
    setOrgContext,
    getPreviousStepId,
  } = tracking;

  useEffect(() => {
    if (authError) {
      if (!isEmailNotVerifiedError(authError)) {
        alert.post({
          message: `Failed to log in: ${authError.message}`,
          severity: 'error',
        });
      }
    }
  }, [authError, alert]);

  useEffect(() => {
    if (skipCompanyWizard) {
      setStep(currentStep => Math.max(currentStep, 2));
    }
  }, [skipCompanyWizard]);

  // Form
  const signupAttribution = useMemo(
    () => getSignupAttribution(searchParams),
    [searchParams],
  );

  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [acceptMarketingEmails, setAcceptMarketingEmails] =
    useState<boolean>(true);
  const [accountDetails, setAccountDetails] =
    useSessionStorage<AccountDetailsState>(
      `signup-${inviteId ?? 'regular'}`,
      DEFAULT_STATE,
    );

  useEffect(() => {
    if (createdAccount) {
      trackCompletion('success');
    }
  }, [createdAccount, trackCompletion]);

  const handleResultDismissed = useCallback(() => {
    setShowOverlay(false);
    if (createdAccount) {
      onCreate(createdAccount);
      setAccountDetails(DEFAULT_STATE);
    }
  }, [createdAccount, onCreate, setAccountDetails]);

  // Track errors during account creation
  useEffect(() => {
    if (error) {
      trackCompletion('error', {
        error_message: error.message,
        final_step_id: 'workspace_wizard',
      });
    }
  }, [error, trackCompletion]);

  const setField = useCallback(
    (newData: Partial<AccountDetailsState>) => {
      setAccountDetails(current => ({
        ...current,
        ...newData,
      }));
    },
    [setAccountDetails],
  );

  const toggleAuthConnection = (connection: AuthConnection) => {
    return (_e: SyntheticEvent<EventTarget>, checked: boolean) => {
      setField({
        authConnections: {
          ...accountDetails.authConnections,
          [connection]: checked,
        },
      });
    };
  };

  useEffect(() => {
    if (accountInvite) {
      setField({
        adminEmail: accountInvite.email,
        loginDomain: accountInvite.autoJoinDomain,
      });
    } else if (user) {
      setAccountDetails(current => {
        const updates: Partial<AccountDetailsState> = {};

        if (user.email) {
          updates.adminEmail = user.email;
        }

        // For regular signup (no inviteId), enable both auth connections
        if (!inviteId) {
          updates.authConnections = {
            googleAuthConnection: true,
            passwordAuthConnection: true,
          };
        } else {
          // For invite signup, pre-select the auth connection the user signed up with
          // but they can change it in the UI step
          if (user.sub?.includes('google')) {
            updates.authConnections = {
              ...current.authConnections,
              googleAuthConnection: true,
            };
          } else {
            updates.authConnections = {
              ...current.authConnections,
              passwordAuthConnection: true,
            };
          }
        }

        return { ...current, ...updates };
      });
    }
  }, [accountInvite, user, inviteId, setField, setAccountDetails]);

  const hasSelectedAuthConnection = Object.values(
    accountDetails.authConnections,
  ).some(Boolean);

  const userAuthConnection: AuthConnection = user?.sub?.includes(
    'google-oauth2',
  )
    ? 'googleAuthConnection'
    : 'passwordAuthConnection';

  //   Account name validation
  const [availabilityState, checkAvailability] = useValidateAccountName();
  const [loginId, setLoginId] = useState('');
  const [modified, setModified] = useState<string[]>([]);

  // Keep tracking org context in sync with form data
  useEffect(() => {
    setOrgContext({
      display_name: accountDetails.displayName || undefined,
      workspace_name: loginId || undefined,
    });
  }, [accountDetails.displayName, loginId, setOrgContext]);

  useDebounce(
    () => {
      if (!isLoginIdValid(loginId)) return;
      checkAvailability({
        loginId: loginId,
        inviteId: inviteId,
      });
    },
    500,
    [loginId],
  );

  const loginIdAvailable = availabilityState.value
    ? (availabilityState.value.available ?? true)
    : true;

  const loginIdError = availabilityState.value
    ? availabilityState.value.message
    : false;

  const validCompanyName = modified.includes('companyName')
    ? isDisplayNameValid(accountDetails.displayName)
    : true;

  let companyNameHelperText =
    "This is typically your company name. It can't be changed later.";
  if (modified.includes('companyName') && !validCompanyName) {
    if (!/^[a-zA-Z]/.test(accountDetails.displayName)) {
      companyNameHelperText = 'The name should start with a letter.';
    } else if (
      accountDetails.displayName.endsWith('-') ||
      accountDetails.displayName.endsWith(' ')
    ) {
      companyNameHelperText = `The name cannot end with "-" or a space.`;
    } else {
      companyNameHelperText = `The name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`;
    }
  }

  // Workspace name validation
  const validWorkspaceName = modified.includes('workspaceName')
    ? isLoginIdValid(loginId)
    : true;

  let workspaceNameHelperText =
    "This is used when logging in to Confidence, typically your-company-name. It can't be changed later.";
  if (!loginIdAvailable) {
    workspaceNameHelperText = 'This workspace name is already in use.';
  } else if (availabilityState.loading) {
    workspaceNameHelperText = 'Checking availability...';
  } else if (!!loginIdError) {
    workspaceNameHelperText = loginIdError;
  } else if (modified.includes('workspaceName') && loginId.endsWith('-')) {
    workspaceNameHelperText = `The name cannot end with "-".`;
  } else if (!validWorkspaceName) {
    workspaceNameHelperText = `The name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`;
  }

  // Don't allow navigating through the form by changing the URL
  useEffect(() => {
    searchParams.delete('step');
    setSearchParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track step changes
  useEffect(() => {
    if (!isAuthenticated) return;

    const stepConfigs = [
      { step_id: 'business_screen', step_name: 'Business Screen' },
      { step_id: 'user_info', step_name: 'User Info Wizard' },
      { step_id: 'workspace_intro', step_name: 'Workspace Introduction' },
      { step_id: 'workspace_wizard', step_name: 'Workspace Wizard' },
    ];

    const config = stepConfigs[step];
    if (config) {
      trackStep('impression', {
        step_id: config.step_id,
        step_name: config.step_name,
        step_index: step,
        step_count: 4,
        navigation: {
          entry_mode: 'next',
          previous_step_id: getPreviousStepId(),
        },
      });
    }
  }, [step, isAuthenticated, trackStep, getPreviousStepId]);

  // User sign in
  const handleLogin = async () => {
    await loginWithPopup(
      { authorizationParams: { screen_hint: 'signup' } },
      {
        timeoutInSeconds: 600,
      },
    );
  };

  // Create account
  const handleCreate = () => {
    if (!user) return;
    setShowOverlay(true);

    createAccount(
      {
        displayName: accountDetails.displayName,
        loginId: loginId,
        region: accountDetails.region,
        authConnections: Object.entries(accountDetails.authConnections)
          .filter(([_, v]) => v)
          .map(([connection]) => ({
            [connection]: {},
          })) as CreateAccountRequest['authConnections'],
        adminEmail:
          accountDetails.adminEmail !== ''
            ? accountDetails.adminEmail
            : undefined,
        allowedLoginEmailDomains:
          inviteId && accountDetails.authConnections.googleAuthConnection
            ? [accountDetails.loginDomain].filter(Boolean)
            : undefined,
        marketingOptIn: acceptMarketingEmails,
        userRole: accountDetails.userRole || undefined,
        userGoals:
          accountDetails.userGoals.length > 0
            ? accountDetails.userGoals
            : undefined,
        signupAttribution,
      },
      token,
    );
  };

  // Check if user needs to verify their email before proceeding
  const isEmailVerified = user?.email_verified ?? false;

  // Track verify email screen impression
  const hasTrackedVerifyEmailRef = useRef(false);
  useEffect(() => {
    if (
      isAuthenticated &&
      !isEmailVerified &&
      !hasTrackedVerifyEmailRef.current
    ) {
      hasTrackedVerifyEmailRef.current = true;
      trackStep('impression', {
        step_id: 'verify_email',
        step_name: 'Verify Email',
        step_index: -1, // Before the regular wizard steps
        step_count: 5, // 4 wizard steps + 1 verify email
        navigation: {
          entry_mode: 'direct',
          previous_step_id: getPreviousStepId(),
        },
      });
    }
  }, [isAuthenticated, isEmailVerified, trackStep, getPreviousStepId]);

  // Auto-check email verification when user returns to the tab
  useEffect(() => {
    if (!isAuthenticated || isEmailVerified) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force a fresh token fetch to get updated email_verified claim
        getAccessTokenSilently({ cacheMode: 'off' }).catch(() => {
          // Silently ignore errors - user can still click the button
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isEmailVerified, getAccessTokenSilently]);

  if (isLoading) return <LoadingOverlay />;

  if (isAuthenticated && !isEmailVerified) {
    const handleVerificationCheck = async () => {
      // Force a fresh token fetch to get updated email_verified claim
      await getAccessTokenSilently({ cacheMode: 'off' });
      // Page will re-render with new user object if email is now verified
    };

    return (
      <Dialog open fullScreen>
        <Branding />
        {user && <UserInfoButton user={user} />}
        <TransitionScreen
          title="Verify your e-mail address"
          description="We've sent you a verification email. Please click the link in the email to verify your address."
          actions={
            <Button
              variant="contained"
              color="primary"
              onClick={handleVerificationCheck}
            >
              I&apos;ve verified my email
            </Button>
          }
          helperText={
            <>
              Didn&apos;t receive a verification e-mail?{' '}
              <Link
                underline="always"
                href="mailto:confidence-support@spotify.com"
                target="_blank"
              >
                Contact support
              </Link>
              .
            </>
          }
        />
      </Dialog>
    );
  }

  if (isAuthenticated && isAccountUnderReviewError(error)) {
    return (
      <Dialog open fullScreen>
        <Branding />
        {user && <UserInfoButton user={user} />}
        <TransitionScreen
          title="Thank you for signing up for Confidence"
          description="We are currently reviewing your account request. You will receive an email once it has been approved."
          helperText={
            <>
              Have questions?{' '}
              <Link
                underline="always"
                href="mailto:confidence-support@spotify.com"
                target="_blank"
              >
                Contact support
              </Link>
              .
            </>
          }
        />
      </Dialog>
    );
  }

  if (isAuthenticated) {
    return (
      <Dialog open fullScreen>
        <Branding />
        {user && <UserInfoButton user={user} />}
        {step === 0 && (
          <IntroStep
            title={
              <>
                Confidence is made for businesses.
                <br /> Ready to dive in?
              </>
            }
            description="We'll start by asking you some information on your organization!"
            buttonLabel="Let's go"
            img={MadeForBusinesses}
            onNext={() => {
              setStep(1);
            }}
          />
        )}
        {step === 1 && (
          <WizardStepper
            submitLabel="Next"
            onSubmit={() => {
              // Track user intent with enhanced tracking
              trackUserIntent(
                accountDetails.userRole,
                accountDetails.userGoals,
              );
              searchParams.delete('step');
              setSearchParams(searchParams);
              setStep(2);
            }}
            steps={[
              {
                name: 'What describes you best?',
                description:
                  'Help us understand your role so we can personalize your experience.',
                disableNext: !accountDetails.userRole,
                content: (
                  <UserRole
                    value={accountDetails.userRole}
                    onChange={role => setField({ userRole: role })}
                  />
                ),
              },
              {
                name: 'What are you here to do?',
                description:
                  'Let us know your goals so we can help you get started.',
                disableNext: accountDetails.userGoals.length === 0,
                content: (
                  <UserGoals
                    value={accountDetails.userGoals}
                    onChange={goals => setField({ userGoals: goals })}
                  />
                ),
              },
            ]}
          />
        )}
        {step === 2 && (
          <IntroStep
            title={
              <>
                Let&apos;s configure your <br /> organization&apos;s workspace
              </>
            }
            description="Configure your workspace by answering a few questions."
            buttonLabel="Configure workspace"
            img={CreateWorkspace}
            onNext={() => {
              searchParams.delete('step');
              setStep(3);
            }}
          />
        )}
        {step === 3 && (
          <WizardStepper
            submitLabel="Create workspace"
            onSubmit={handleCreate}
            loading={showOverlay}
            loadingContent={
              <ExperimentationLoader
                completed={!!createdAccount}
                error={error}
                onResultDismissed={handleResultDismissed}
              />
            }
            error={error}
            steps={[
              {
                name: 'Workspace details',
                description:
                  'Set your name and workspace name. The workspace is where you and your team set up, run and analyze experiments.',
                disableNext:
                  !loginId ||
                  !loginIdAvailable ||
                  !validCompanyName ||
                  !validWorkspaceName ||
                  !accountDetails.displayName,
                content: (
                  <FormFieldsBox spacing={2}>
                    <TextField
                      variant="outlined"
                      label="Name"
                      placeholder="For example My Company"
                      value={accountDetails.displayName}
                      helperText={companyNameHelperText}
                      error={!validCompanyName}
                      required
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const formattedName = formatDisplayName(e.target.value);
                        // Auto-populate workspace name from name if not manually modified
                        if (!modified.includes('workspaceName')) {
                          const formattedLoginId = formatLoginId(formattedName);
                          setLoginId(formattedLoginId);
                          setField({
                            displayName: formattedName,
                          });
                        } else {
                          setField({
                            displayName: formattedName,
                          });
                        }
                      }}
                      onBlur={() => {
                        if (!modified.includes('companyName')) {
                          setModified(current => current.concat('companyName'));
                        }
                      }}
                      fullWidth
                    />

                    <TextField
                      helperText={workspaceNameHelperText}
                      variant="outlined"
                      label="Workspace name"
                      required
                      placeholder="For example my-company"
                      value={loginId}
                      error={
                        !loginIdAvailable || !validWorkspaceName || loginIdError
                      }
                      name="workspaceName"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const formattedName = formatLoginId(e.target.value);
                        setLoginId(formattedName);
                      }}
                      onBlur={() => {
                        if (!modified.includes('workspaceName')) {
                          setModified(current =>
                            current.concat('workspaceName'),
                          );
                        }
                      }}
                      inputProps={{
                        maxLength: NAME_MAX_LENGTH,
                      }}
                      InputProps={{
                        endAdornment: availabilityState.loading && (
                          <CircularProgress size="1rem" />
                        ),
                      }}
                      fullWidth
                    />
                  </FormFieldsBox>
                ),
              },

              // Only show auth configuration for invitation flow
              ...(inviteId
                ? [
                    {
                      name: 'Set up how to log into Confidence',
                      description:
                        'Choose the authentication methods that are available for people in your company to access Confidence.',
                      disableNext: !hasSelectedAuthConnection,
                      content: (
                        <>
                          <FormControl component="fieldset">
                            <FormControlLabel
                              value="passwordAuthConnection"
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={
                                    accountDetails.authConnections
                                      .passwordAuthConnection
                                  }
                                  required={!hasSelectedAuthConnection}
                                  disabled={
                                    userAuthConnection ===
                                    'passwordAuthConnection'
                                  }
                                />
                              }
                              onChange={toggleAuthConnection(
                                'passwordAuthConnection',
                              )}
                              label={`E-mail ${
                                userAuthConnection === 'passwordAuthConnection'
                                  ? '(you used this when signing up)'
                                  : ''
                              }`}
                            />
                            <FormControlLabel
                              value="googleAuthConnection"
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={
                                    accountDetails.authConnections
                                      .googleAuthConnection
                                  }
                                  required={!hasSelectedAuthConnection}
                                  disabled={
                                    userAuthConnection ===
                                    'googleAuthConnection'
                                  }
                                />
                              }
                              onChange={toggleAuthConnection(
                                'googleAuthConnection',
                              )}
                              label={`Google ${
                                userAuthConnection === 'googleAuthConnection'
                                  ? '(you used this when signing up)'
                                  : ''
                              }`}
                            />
                          </FormControl>

                          <Collapse
                            in={
                              accountDetails.authConnections
                                .googleAuthConnection
                            }
                          >
                            <Box marginTop={4}>
                              <InputLabel>Google login options</InputLabel>
                              <FormControl component="fieldset">
                                <FormControlLabel
                                  value="invite-only"
                                  control={
                                    <Radio
                                      color="primary"
                                      checked={
                                        accountDetails.loginDomain === undefined
                                      }
                                    />
                                  }
                                  onChange={() =>
                                    setField({ loginDomain: undefined })
                                  }
                                  label="Only invited users can log in"
                                />
                                <FormControlLabel
                                  value="invite-only"
                                  control={
                                    <Radio
                                      color="primary"
                                      checked={
                                        accountDetails.loginDomain !== undefined
                                      }
                                    />
                                  }
                                  onChange={() => setField({ loginDomain: '' })}
                                  label={
                                    <>
                                      Allow everyone from a specific domain to
                                      login
                                    </>
                                  }
                                />
                                <Collapse
                                  in={accountDetails.loginDomain !== undefined}
                                >
                                  <TextField
                                    label="Google login domain"
                                    variant="outlined"
                                    name="google-login-domain"
                                    placeholder="For example spotify.com"
                                    value={
                                      accountDetails.authConnections
                                        .googleAuthConnection
                                        ? accountDetails.loginDomain
                                        : ''
                                    }
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                      setField({ loginDomain: e.target.value })
                                    }
                                    disabled={
                                      !accountDetails.authConnections
                                        .googleAuthConnection
                                    }
                                    fullWidth
                                  />
                                </Collapse>
                              </FormControl>
                            </Box>
                          </Collapse>
                        </>
                      ),
                    },
                  ]
                : []),
              {
                name: 'Choose a region for storing your data',
                description:
                  'Confidence can store your data in your different regions around the world. ' +
                  'Storing data at the right place in the world is important because of data regulations. ' +
                  'Select the region that best fits your company. If you are unsure, select EU.',
                disableNext: !accountDetails.region,
                content: (
                  <FormControl component="fieldset">
                    <FormLabel required>Region</FormLabel>
                    <RadioGroup
                      aria-label="region"
                      name="region"
                      value={accountDetails.region}
                      onChange={(_e, region) =>
                        setField({ region: region as Region })
                      }
                    >
                      <FormControlLabel
                        value="REGION_EU"
                        control={<Radio color="primary" />}
                        label="EU"
                      />
                      <FormControlLabel
                        value="REGION_US"
                        control={<Radio color="primary" />}
                        label="US"
                      />
                    </RadioGroup>
                    <FormHelperText>
                      Note: this can&apos;t be changed later.
                    </FormHelperText>
                  </FormControl>
                ),
              },
              {
                name: 'And finally: Terms and conditions',
                disableNext: !acceptTerms,
                content: (
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={acceptTerms}
                          onChange={(_e, checked) => setAcceptTerms(checked)}
                          name="terms-of-service"
                        />
                      }
                      label={
                        <>
                          I agree to the{' '}
                          <ConfidenceWebsiteLink
                            underline="always"
                            route="/terms-of-service"
                          >
                            terms of service
                          </ConfidenceWebsiteLink>
                          .
                        </>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={acceptMarketingEmails}
                          onChange={(_e, checked) =>
                            setAcceptMarketingEmails(checked)
                          }
                          name="marketing-emails"
                        />
                      }
                      label="Send me tips and updates to help me get started with Confidence"
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      </Dialog>
    );
  }

  if (isEmailNotVerifiedError(authError)) {
    return (
      <div data-track-id="region.verify-email" data-track-impression>
        <TransitionScreen
          title="Verify your e&#8209;mail address"
          description="Please click on the link we sent you to verify your e&#8209;mail address."
          actions={
            <Button variant="contained" color="primary" onClick={handleLogin}>
              Log in
            </Button>
          }
          helperText={
            <>
              Didn&apos;t receive a verification e-mail?{' '}
              <Link
                underline="always"
                href="mailto:confidence-support@spotify.com"
                target="_blank"
              >
                Contact support
              </Link>
              .
            </>
          }
        />
      </div>
    );
  }

  return (
    <div data-track-id="region.welcome" data-track-impression>
      <TransitionScreen
        title="Welcome to Confidence"
        description="Sign up with your work e-mail address, not a personal account."
        actions={
          <Button variant="contained" color="primary" onClick={handleLogin}>
            Sign up
          </Button>
        }
        helperText={
          <>
            <strong>Already using Confidence in your company?</strong>
            <br />
            Ask a teammate for an invite or go to the{' '}
            <Link href="/login" underline="always">
              login page
            </Link>{' '}
            if single sign-on is enabled.
          </>
        }
      />
    </div>
  );
};
